import {
  AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges,
  OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { defaultIfEmpty, take } from 'rxjs/operators';
import { SystemBars } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { ApiService } from '../../services/api.service';
import { PlatformService } from '../../services/platform.service';
import { BackButtonService, BackPriority } from '../../services/back-button.service';
import { DevicePresence, DevicePresenceService, presenceLabel } from '../../services/device-presence.service';
import { DeviceLiveDescriptor, mseFallbackUrl } from '../../models/deviceLive';
import { FileRenderer } from './renderers/file-renderer';
import { HlsRenderer } from './renderers/hls-renderer';
import { Mpeg1Renderer } from './renderers/mpeg1-renderer';
import { MseRenderer } from './renderers/mse-renderer';
import { VideoRenderer, VideoRendererHost } from './renderers/video-renderer';
import { WebRtcRenderer } from './renderers/webrtc-renderer';

export type VideoPlayerMode = 'live' | 'playback';
type RendererKind = 'webrtc' | 'mse' | 'mpeg1' | 'hls' | 'file';

// Lets a retrying host tell a configuration error from a transient one.
export interface StreamFailure {
  credentialsRejected: boolean;
}

export interface VideoStatusBadge {
  tone: 'live' | 'down' | 'wait';
  key: string;
  count: number;
}

// Global class (styles.scss): the in-app fullscreen used on native, where a
// WebView has no usable Fullscreen API.
const OVERLAY_CLASS: string = 'uv-video-overlay';

// go2rtc's reason when the camera rejects its RTSP credentials.
const CREDENTIALS_REJECTED: string = 'wrong user/pass';

interface RendererStep {
  kind: RendererKind;
  create: (host: VideoRendererHost) => VideoRenderer;
}

// Single player for every video surface. The descriptor picks the live renderer
// (WebRTC, MSE as fallback, jsmpeg while Live:Transport is mpeg1); playback is
// HLS. Stall detection, viewport gating and the recovery budget live here
// because they are the same whatever decodes the frames.
@Component({
  selector: 'urban-video-player',
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss',
  standalone: false
})
export class VideoPlayerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() mode: VideoPlayerMode = 'live';
  @Input() live: DeviceLiveDescriptor | null = null;
  @Input() playbackUrl: string | null = null;
  // jsmpeg ring buffer; ignored by every other renderer.
  @Input() videoBufferSize?: number;
  @Input() icons: Record<string, SafeHtml> = {};
  @Input() disconnectWhenHidden: boolean = true;
  @Input() visibilityThreshold: number = 0.1;
  // Live only. No frame within this window means this renderer will not work:
  // fall back, or declare the stream dead.
  @Input() connectionTimeoutMs: number = 8000;
  // The camera shown: its heartbeats say whether it is reachable at all.
  @Input() deviceId: string | null | undefined = null;

  // Hooks for hosts that can recover, e.g. by requesting a fresh stream link.
  @Output() streamFailed = new EventEmitter<StreamFailure>();
  @Output() streamStarted = new EventEmitter<void>();

  @ViewChild('video', { static: false }) private videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('legacyWrapper', { static: false }) private legacyWrapperRef!: ElementRef<HTMLDivElement>;
  @ViewChild('playerContainer', { static: false }) private playerContainerRef!: ElementRef<HTMLDivElement>;

  // Async callbacks mutate plain flags; markForCheck refreshes an OnPush host.
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platform = inject(PlatformService);
  private readonly backButton = inject(BackButtonService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly presenceService = inject(DevicePresenceService);
  private readonly api = inject(ApiService);

  public videoLoaded: boolean = false;
  public connectionFailed: boolean = false;
  public credentialsRejected: boolean = false;
  public isPlaying: boolean = false;
  public buffering: boolean = false;
  // Chromium and Firefox controls draw their own loader over the video, WebKit
  // only a small one in the control bar.
  public readonly showBufferingSpinner: boolean = 'webkitSetPresentationMode' in HTMLVideoElement.prototype;
  public currentKind: RendererKind | null = null;
  public presence: DevicePresence | null = null;

  private renderer: VideoRenderer | null = null;
  private chain: RendererStep[] = [];
  private chainIndex: number = 0;
  private presenceSubscription: Subscription | null = null;
  private destroyed: boolean = false;
  // Bumped on every teardown: a renderer torn down mid-handshake can still fire
  // one last callback, and acting on it would advance the fallback chain twice.
  private generation: number = 0;
  private connectionTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  // A frozen picture raises no event, so frames are watched directly and the
  // renderer rebuilt. Capped, so a broken source doesn't loop forever.
  private watchdogHandle: ReturnType<typeof setInterval> | null = null;
  private stallRecoveries: number = 0;
  private lastRecoveryAt: number = 0;
  private readonly stallTimeoutMs: number = 6000;
  private readonly maxStallRecoveries: number = 5;

  // A camera can turn down a reconnect it accepted a minute earlier (seen on
  // the iPhone, 2026-10-20), so one rejection is not proof the credentials are
  // wrong. Retried once per stream; a real mismatch costs one extra attempt.
  private credentialsRetried: boolean = false;
  private credentialsRetryHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly credentialsRetryMs: number = 2000;

  // A live link is a token with a lifetime: a stream restarted after it (back
  // into view, the Live tab of a page left open) finds it gone. One fresh link
  // per stream, asked for only once every renderer has failed.
  private linkRefreshed: boolean = false;
  private linkRequest: Subscription | null = null;

  private intersectionObserver: IntersectionObserver | null = null;
  private isInViewport: boolean = false;
  private isTabVisible: boolean = !document.hidden;
  private isAppActive: boolean = this.platform.isForeground;
  private pausedByVisibility: boolean = false;
  // Scrolling past the player fires rapid intersect/leave pairs; reacting to
  // each one thrashes the relay with connect/disconnect cycles.
  private visibilityDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly visibilityDebounceMs: number = 300;
  private readonly onVisibilityChange = (): void => {
    this.isTabVisible = !document.hidden;
    this.scheduleVisibilityUpdate();
  };
  // The body class follows the document, not the player's button: ESC and the
  // browser's own controls leave fullscreen too.
  private readonly onFullscreenChange = (): void => {
    document.body.classList.toggle('fullscreen-video', this.fullscreenElement() !== null);
  };

  private overlayElement: HTMLElement | null = null;
  private releaseBackButton: (() => void) | null = null;

  public ngAfterViewInit(): void {
    this.setupVisibilityObservers();
    this.platform.appState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isActive => this.onAppStateChange(isActive));

    if (!this.platform.isNative) {
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    }

    this.rebuild();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceId'] && changes['deviceId'].previousValue !== this.deviceId) {
      this.watchPresence();
    }

    if (changes['live']) {
      this.linkRefreshed = false;
    }

    if (changes['live'] || changes['playbackUrl'] || changes['mode'] || changes['videoBufferSize']) {
      this.rebuild();
    }
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.presenceSubscription?.unsubscribe();
    this.linkRequest?.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    this.exitOverlay();

    // Removing the fullscreen element ends fullscreen, but only after the
    // listener above is gone.
    const fullscreen: Element | null = this.fullscreenElement();
    if (fullscreen && fullscreen === this.fullscreenTarget()) {
      document.body.classList.remove('fullscreen-video');
    }

    if (this.visibilityDebounceHandle) {
      clearTimeout(this.visibilityDebounceHandle);
      this.visibilityDebounceHandle = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    this.stop();
  }

  // ---- Template state -------------------------------------------------------

  // jsmpeg owns a canvas; every other renderer draws into <video>.
  public get useVideoElement(): boolean {
    return this.currentKind !== 'mpeg1';
  }

  // Frames arriving beat the heartbeats: a camera that streams is LIVE even
  // when its gateway cannot reach it (the line under the name says so). A
  // failed stream of a reachable camera says so in the middle instead.
  public get statusBadge(): VideoStatusBadge | null {
    if (this.mode !== 'live') {
      return null;
    }

    const offline: boolean = !!this.presence?.gatewayName && this.presence.presence === 'offline';

    if (this.videoLoaded && !this.connectionFailed) {
      return this.isPlaying ? { tone: 'live', key: 'VIDEO_STATUS.LIVE', count: 0 } : null;
    }

    if (offline) {
      return { tone: 'down', ...presenceLabel(this.presence!) };
    }

    return this.renderer && !this.connectionFailed
      ? { tone: 'wait', key: 'VIDEO_STATUS.CONNECTING', count: 0 }
      : null;
  }

  public get showPlaybackHint(): boolean {
    return this.mode === 'playback' && !this.playbackUrl;
  }

  public get showControls(): boolean {
    return this.mode === 'live' && this.videoLoaded && !this.connectionFailed;
  }

  // Only when the controls are hidden (stream loading, failed, or playback):
  // their fullscreen button already closes the overlay, and iOS has no back
  // button to fall back on.
  public get showOverlayClose(): boolean {
    return this.overlayElement !== null && !this.showControls;
  }

  private watchPresence(): void {
    this.presenceSubscription?.unsubscribe();
    this.presence = null;
    this.presenceSubscription = this.presenceService.watch(this.deviceId).subscribe(presence => {
      this.presence = presence;
      this.cdr.markForCheck();
    });
  }

  // ---- Renderer lifecycle ---------------------------------------------------

  private rebuild(): void {
    if (!this.videoRef?.nativeElement) {
      return; // before the view exists; ngAfterViewInit runs it
    }

    this.stop();

    this.chain = this.buildChain();
    this.chainIndex = 0;
    this.stallRecoveries = 0;
    this.connectionFailed = false;
    this.credentialsRejected = false;
    this.credentialsRetried = false;
    this.videoLoaded = false;
    this.buffering = false;

    if (!this.chain.length) {
      // A resolved descriptor with no Url means this camera has no live source,
      // so say so. A missing descriptor is still in flight: keep waiting.
      this.connectionFailed = this.mode === 'live' && !!this.live && !this.live.Url;
      this.cdr.markForCheck();
      return;
    }

    if (this.mode === 'live' && !this.shouldBeActive()) {
      this.cdr.markForCheck();
      return; // out of sight: the next visibility update starts it
    }

    this.startCurrent();
  }

  // Live: WebRTC first, MSE when the media port is unreachable. mpeg1 has no
  // fallback - it is the fallback, until it is dismissed.
  private buildChain(): RendererStep[] {
    const video: HTMLVideoElement = this.videoRef.nativeElement;

    if (this.mode === 'playback') {
      const url: string | null = this.playbackUrl;
      if (!url) {
        return [];
      }
      // An HLS playlist is exported from the camera on demand; anything else is a finished file.
      return new URL(url, location.href).pathname.endsWith('.m3u8')
        ? [{ kind: 'hls', create: host => new HlsRenderer(video, host, url) }]
        : [{ kind: 'file', create: host => new FileRenderer(video, host, url) }];
    }

    const url: string | null = this.live?.Url ?? null;

    if (!url) {
      return [];
    }

    if (this.live?.Transport === 'webrtc') {
      return [
        { kind: 'webrtc', create: host => new WebRtcRenderer(video, host, url) },
        { kind: 'mse', create: host => new MseRenderer(video, host, mseFallbackUrl(url)) }
      ];
    }

    return [{
      kind: 'mpeg1',
      create: host => new Mpeg1Renderer(this.legacyWrapperRef.nativeElement, host, url, this.videoBufferSize)
    }];
  }

  private startCurrent(): void {
    const step: RendererStep | undefined = this.chain[this.chainIndex];

    if (!step) {
      this.markFailed();
      return;
    }

    const generation: number = this.generation;

    this.currentKind = step.kind;
    this.videoLoaded = false;
    this.credentialsRejected = false;
    this.isPlaying = true;

    this.renderer = step.create({
      onStarted: () => { if (generation === this.generation) this.onStarted(); },
      onFatal: (reason, terminal) => {
        if (generation === this.generation) this.onFatal(reason, terminal);
      }
    });
    this.renderer.start();

    // Live only: a playback segment is exported from the camera on demand, and
    // hls.js already waits up to a minute for it (fragLoadingTimeOut).
    if (this.mode === 'live') {
      this.connectionTimeoutHandle = setTimeout(() => {
        if (generation === this.generation && !this.videoLoaded) {
          this.onFatal('no frame before the connection timeout');
        }
      }, this.connectionTimeoutMs);
    }

    this.startStallWatchdog();
    this.cdr.markForCheck();
  }

  private stop(): void {
    this.generation++;
    this.clearConnectionTimeout();
    this.stopStallWatchdog();

    if (this.credentialsRetryHandle) {
      clearTimeout(this.credentialsRetryHandle);
      this.credentialsRetryHandle = null;
    }

    if (this.renderer) {
      this.renderer.destroy();
      this.renderer = null;
    }

    this.currentKind = null;
    this.videoLoaded = false;
    this.isPlaying = false;
    this.cdr.markForCheck();
  }

  private markFailed(): void {
    this.stop();

    if (this.refreshLink()) {
      return;
    }

    this.declareFailed();
  }

  private declareFailed(): void {
    this.connectionFailed = true;
    this.cdr.markForCheck();
    this.streamFailed.emit({ credentialsRejected: this.credentialsRejected });
  }

  // True when a fresh link was asked for. The backend hands out the same link
  // while its token lives, so a different one means the old had expired and the
  // stream starts again; the same one means the failure is elsewhere.
  private refreshLink(): boolean {
    const current: string | null = this.live?.Url ?? null;

    if (this.mode !== 'live' || !current || !this.deviceId || this.linkRefreshed || this.credentialsRejected) {
      return false;
    }

    this.linkRefreshed = true;
    this.linkRequest?.unsubscribe();
    this.linkRequest = this.api.getLiveDescriptor(this.deviceId).pipe(
      take(1),
      defaultIfEmpty(null)
    ).subscribe(descriptor => {
      // The host may have handed over another link, or the tab switched, meanwhile.
      if (this.destroyed || this.live?.Url !== current) {
        return;
      }

      if (!descriptor?.Url || descriptor.Url === current) {
        if (this.mode === 'live') {
          this.declareFailed();
        }
        return;
      }

      this.live = descriptor;

      if (this.mode === 'live') {
        this.rebuild();
      }
    });

    return true;
  }

  // ---- Renderer callbacks ---------------------------------------------------

  private onStarted(): void {
    if (this.destroyed || this.videoLoaded) {
      return;
    }

    this.clearConnectionTimeout();
    this.videoLoaded = true;
    this.isPlaying = true;
    this.connectionFailed = false;
    this.credentialsRetried = false;
    this.linkRefreshed = false;
    this.cdr.markForCheck();
    this.streamStarted.emit();
  }

  private onFatal(reason: string, terminal: boolean = false): void {
    if (this.destroyed || this.connectionFailed) {
      return;
    }

    console.warn(`[video-player] ${this.currentKind}: ${reason}`);
    this.credentialsRejected = reason.includes(CREDENTIALS_REJECTED);

    if (this.credentialsRejected && !this.credentialsRetried) {
      this.credentialsRetried = true;
      this.stop(); // any later stop() cancels the retry
      this.credentialsRetryHandle = setTimeout(() => {
        this.credentialsRetryHandle = null;
        // A visibility change may have started a renderer meanwhile, or the
        // stream may have been declared dead: a second start would stack on it.
        if (this.renderer || this.connectionFailed) {
          return;
        }
        this.chainIndex = 0;
        this.startCurrent();
      }, this.credentialsRetryMs);
      return;
    }

    // A terminal failure is upstream of the transport, so the rest of the chain
    // would fail the same way.
    const next: number = terminal ? this.chain.length : this.chainIndex + 1;

    this.stop();

    if (next < this.chain.length) {
      this.chainIndex = next;
      this.stallRecoveries = 0;
      this.startCurrent();
      return;
    }

    this.markFailed();
  }

  // ---- Stall watchdog -------------------------------------------------------

  private startStallWatchdog(): void {
    this.stopStallWatchdog();

    // Playback recovers inside its own renderer, and a paused clip is not a stall.
    if (this.mode !== 'live') {
      return;
    }

    this.watchdogHandle = setInterval(() => {
      if (!this.videoLoaded || !this.isPlaying || !this.shouldBeActive() || !this.renderer) {
        return;
      }

      if (Date.now() - this.renderer.lastFrameAt() < this.stallTimeoutMs) {
        // Refund the budget after a healthy stretch, so glitches spread over a
        // long session don't exhaust it.
        if (this.stallRecoveries > 0 && Date.now() - this.lastRecoveryAt > 60000) {
          this.stallRecoveries = 0;
        }
        return;
      }

      if (this.stallRecoveries >= this.maxStallRecoveries) {
        this.onFatal('stalled beyond the recovery budget');
        return;
      }

      this.stallRecoveries++;
      this.lastRecoveryAt = Date.now();
      this.stop();
      this.startCurrent();
    }, 2000);
  }

  private stopStallWatchdog(): void {
    if (this.watchdogHandle) {
      clearInterval(this.watchdogHandle);
      this.watchdogHandle = null;
    }
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutHandle) {
      clearTimeout(this.connectionTimeoutHandle);
      this.connectionTimeoutHandle = null;
    }
  }

  // ---- Viewport and tab visibility -----------------------------------------

  private setupVisibilityObservers(): void {
    const target: HTMLElement | undefined = this.playerContainerRef?.nativeElement;

    if (!target) {
      return;
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            this.isInViewport = entry.isIntersecting;
          }
          this.scheduleVisibilityUpdate();
        },
        { threshold: this.visibilityThreshold }
      );
      this.intersectionObserver.observe(target);
    } else {
      this.isInViewport = true;
    }
  }

  private shouldBeActive(): boolean {
    return this.isInViewport && this.isTabVisible && this.isAppActive;
  }

  // Not debounced: once the app is in background the WebView may be suspended
  // before a timer fires, leaving the stream open. Live always disconnects here,
  // whatever disconnectWhenHidden says; a recording is only paused.
  private onAppStateChange(isActive: boolean): void {
    this.isAppActive = isActive;

    if (isActive) {
      this.scheduleVisibilityUpdate();
      return;
    }

    if (this.visibilityDebounceHandle) {
      clearTimeout(this.visibilityDebounceHandle);
      this.visibilityDebounceHandle = null;
    }

    if (this.mode === 'playback') {
      this.renderer?.pause();
      return;
    }

    if (this.chain.length) {
      this.stop();
      this.pausedByVisibility = true;
    }
  }

  private scheduleVisibilityUpdate(): void {
    if (this.visibilityDebounceHandle) {
      clearTimeout(this.visibilityDebounceHandle);
    }

    this.visibilityDebounceHandle = setTimeout(() => {
      this.visibilityDebounceHandle = null;
      this.applyVisibilityState();
    }, this.visibilityDebounceMs);
  }

  // Only live is gated: it holds an upstream RTSP session for as long as it
  // runs, a paused clip costs nothing. Coming back into view also retries a
  // failed stream - the natural moment, and bounded by the user's scrolling.
  private applyVisibilityState(): void {
    if (this.mode !== 'live' || !this.chain.length) {
      return;
    }

    if (!this.shouldBeActive()) {
      if (this.disconnectWhenHidden) {
        this.stop();
      } else if (this.renderer && this.isPlaying) {
        this.renderer.pause();
        this.isPlaying = false;
      }
      this.pausedByVisibility = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.pausedByVisibility) {
      this.pausedByVisibility = false;

      if (this.renderer) {
        this.renderer.play();
        this.isPlaying = true;
      } else {
        this.chainIndex = 0;
        this.stallRecoveries = 0;
        this.connectionFailed = false;
        this.startCurrent();
      }
    } else if (!this.renderer && !this.connectionFailed) {
      // First time in view: rebuild() deferred the start. A stream that already
      // failed stays failed, or every observer callback would retry it forever.
      this.startCurrent();
    }

    this.cdr.markForCheck();
  }

  // ---- Controls -------------------------------------------------------------

  public togglePlayPause(): void {
    if (!this.renderer) {
      return;
    }

    if (this.isPlaying) {
      this.renderer.pause();
    } else {
      this.renderer.play();
    }

    this.isPlaying = !this.isPlaying;
  }

  public toggleFullscreen(): void {
    const element: HTMLElement | null = this.fullscreenTarget();

    if (!element) {
      return;
    }

    if (this.platform.isNative) {
      if (this.overlayElement) {
        this.exitOverlay();
      } else {
        this.enterOverlay(element);
      }
      return;
    }

    if (this.fullscreenElement()) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen(element);
    }
  }

  // Hosts without a .streamingContainer ancestor use the player's own box.
  private fullscreenTarget(): HTMLElement | null {
    const box: HTMLElement | undefined = this.playerContainerRef?.nativeElement;
    return (box?.closest('.streamingContainer') as HTMLElement | null) ?? box ?? null;
  }

  private enterOverlay(element: HTMLElement): void {
    this.overlayElement = element;
    element.classList.add(OVERLAY_CLASS);
    document.body.classList.add('fullscreen-video');

    ScreenOrientation.lock({ orientation: 'landscape' })
      .catch((error: unknown) => console.warn('[video-player] orientation lock failed:', error));
    // Status and navigation bar (Android nav/gesture bar, iOS home indicator).
    SystemBars.hide()
      .catch((error: unknown) => console.warn('[video-player] system bars hide failed:', error));

    this.releaseBackButton = this.backButton.register(BackPriority.Overlay, () => {
      this.exitOverlay();
      return true;
    });

    this.cdr.markForCheck();
  }

  private exitOverlay(): void {
    if (!this.overlayElement) {
      return;
    }

    this.overlayElement.classList.remove(OVERLAY_CLASS);
    this.overlayElement = null;
    document.body.classList.remove('fullscreen-video');

    void this.restoreOrientation();
    SystemBars.show()
      .catch((error: unknown) => console.warn('[video-player] system bars show failed:', error));

    this.releaseBackButton?.();
    this.releaseBackButton = null;

    this.cdr.markForCheck();
  }

  // On iOS unlock() only widens what is allowed: the screen keeps the landscape
  // geometry until the phone is physically rotated, so aim at it first.
  private async restoreOrientation(): Promise<void> {
    try {
      if (this.platform.platform === 'ios') {
        const current = await ScreenOrientation.orientation();
        await ScreenOrientation.lock({ orientation: current.type });
      }

      await ScreenOrientation.unlock();
    } catch (error: unknown) {
      console.warn('[video-player] orientation restore failed:', error);
    }
  }

  private enterFullscreen(element: HTMLElement): void {
    const legacy = element as unknown as {
      webkitRequestFullscreen?: () => Promise<void>;
      msRequestFullscreen?: () => Promise<void>;
    };
    const request = element.requestFullscreen ?? legacy.webkitRequestFullscreen ?? legacy.msRequestFullscreen;

    if (!request) {
      return;
    }

    request.call(element)
      .catch((error: unknown) => console.error('Error attempting to enter fullscreen:', error));
  }

  private exitFullscreen(): void {
    const legacy = document as unknown as {
      webkitExitFullscreen?: () => Promise<void>;
      msExitFullscreen?: () => Promise<void>;
    };
    const exit = document.exitFullscreen ?? legacy.webkitExitFullscreen ?? legacy.msExitFullscreen;

    if (!exit) {
      return;
    }

    exit.call(document)
      .catch((error: unknown) => console.error('Error attempting to exit fullscreen:', error));
  }

  private fullscreenElement(): Element | null {
    const legacy = document as unknown as {
      webkitFullscreenElement?: Element;
      msFullscreenElement?: Element;
    };
    return document.fullscreenElement ?? legacy.webkitFullscreenElement ?? legacy.msFullscreenElement ?? null;
  }
}
